import {
  Component,
  Input,
  Output,
  EventEmitter,
  SimpleChanges
} from '@angular/core';
import { UserService } from 'src/app/_services/user.service';
import { UtilityService } from 'src/app/_services/utility.service';
import { FormControl } from '@angular/forms';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  switchMap
} from 'rxjs';
import Swal from 'sweetalert2';
import { AuthService } from 'src/app/_services/auth.service';

export interface Users {
  first_name: string;
  last_name: string;
  id: string;
}

/**
 * @description Questo componente genera una select per la selezione di un utente a cui fare una revisione.
 *
 * **Input:**
 * @param {boolean} hidden - Boolean per nascondere la select agli User e/o agli Scrum Master.
 * @param {string} role - Ruolo più alto dell'utente loggato.
 * @param {BehaviorSubject<boolean>} isClearBS - BehaviorSubject per il reset della select.
 * @param {string} user - id dell'utente che vogliamo patchare nella select.
 *
 * **Output:**
 * @param {EventEmitter<number>} chosenUser - EventEmitter per emettere l'id dell'utente selezionato.
 * @param {EventEmitter<boolean>} resetChanges - EventEmitter per emettere un booleano per il reset delle modifiche.
 *
 * **Logica di visibilità della select:**
 * - Se l'utente loggato è un **admin**, la select è visibile e mostra tutti gli utenti.
 * - Se l'utente loggato è un **project manager**, viene mostrata la select con gli utenti che sono sotto di lui.
 * - Se l'utente loggato è uno **scrum master**, la select è visibile solo se l'input `hidden` è `false`.
 * - Se l'utente loggato è uno **user**, la select non è visibile.
 */
@Component({
  selector: 'app-user-select',
  templateUrl: './user-select.component.html',
  styleUrls: ['./user-select.component.css']
})
export class UserSelectComponent {
  @Input() isEditMode: boolean = false;
  @Input() user: any;
  @Input() hidden: boolean = true;
  @Input() role: string = '';

  @Output() chosenUser = new EventEmitter<string>();
  @Output() resetChanges = new EventEmitter<boolean>();
  @Input() isClearBS = new BehaviorSubject<boolean>(true);

  myUserControl = new FormControl();

  chosenDefault: string = 'Selezione utente';
  isClear: any;
  users: any;
  user_select: any;
  firstUsersLength: number = 0;
  changesDetected: boolean = false;
  shouldShowSelect: boolean = false;

  constructor(
    private readonly userService: UserService,
    private readonly utilityService: UtilityService,
    private readonly authService: AuthService
  ) {
    this.isClearBS = this.utilityService.getIsClear();
  }

  ngOnInit() {
    this.AllUser();
    var obj = this.authService.getChosenUser();

    if (obj != null) {
      this.myUserControl.patchValue(obj);
      this.selectUser(obj);
    }

    this.myUserControl.valueChanges
      .pipe(
        debounceTime(1000), // Attesa di 300 ms dopo l'ultima modifica
        distinctUntilChanged(), // Ignora modifiche se sono uguali
        switchMap(filter_search =>
          this.userService.selectUserReview({ filter_search })
        ) // Chiamata al servizio API con la stringa di ricerca
      )
      .subscribe(res => {
        this.users = res.result;
      });

    this.isClearBS.subscribe(value => {
      this.isClear = value;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['role']?.currentValue) {
      this.updateShowSelect();
    }
  }

  /**
   *  @description Mostra la select in base al ruolo dell'utente loggato è in base all'input hidden
   * @returns boolean
   */
  updateShowSelect() {
    if (['admin', 'project_manager', 'team_leader'].includes(this.role)) {
      this.shouldShowSelect = true;
    } else if (this.role === 'user') {
      this.shouldShowSelect = !this.hidden && this.firstUsersLength > 0;
    } else {
      this.shouldShowSelect = false;
    }
  }

  stopEventPropagation(event: Event) {
    event.stopPropagation();
  }

  selectUser(value: any) {
    if (value) {
      this.chosenUser.emit(value?.id);
      this.myUserControl.disable();
    } else {
      this.chosenUser.emit(value);
    }
    this.authService.setChosenUser(value);
  }

  checkClear(value?: any) {
    this.isClear = this.isClearBS.value;
    if (this.isClear == false) this.alert(value);
  }

  resetGuard() {
    this.resetChanges.emit(true);
    this.changesDetected = true;
  }

  alert(value?: any) {
    Swal.fire({
      text: 'Sicuro di voler cambiare utente? Perderai tutte le modifiche non salvate',
      showCancelButton: true,
      cancelButtonText: 'Annulla',
      cancelButtonColor: '#00337C',
      showConfirmButton: true,
      confirmButtonText: 'Conferma',
      confirmButtonColor: '#FFB26B',
      reverseButtons: true,
      customClass: {
        popup: 'main-font'
      },
      backdrop: '#f4f3efad',
      position: 'center',
      target: '.mat-card-content',
      width: '550px'
    }).then(result => {
      if (result.isConfirmed) {
        this.isClearBS.next(true);
        this.resetUserSelect(value);
        this.resetGuard();
        this.changesDetected = false;
      } else this.changesDetected = true;
    });
  }

  resetUserSelect(value?: any) {
    this.myUserControl.enable();
    this.myUserControl.setValue('');
    let user_reset = '';
    this.selectUser(user_reset);
    if (value) this.selectUser(value);
  }

  AllUser() {
    this.userService.selectUserReview().subscribe(res => {
      this.users = res.result;
      this.firstUsersLength = this.users.length;
    });
  }

  displayUsersFn(user: Users): string {
    return user && user.first_name && user.last_name
      ? user.first_name + ' ' + user.last_name
      : '';
  }

  ngOnDestroy() {
    this.utilityService.setIsClear(true);
  }
}
